import React, { Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import AuthGuard from './components/AuthGuard'
import { FacilityOnlyRoute, TrainerOnlyRoute } from './components/TrainerRouteGuard'
import Layout from './components/layout/Layout'
import { PortalErrorBoundary } from './components/PortalErrorBoundary'
import { setUserContext, clearUserContext } from './lib/monitoring'
import { useMonitoring } from './hooks/useMonitoring'
import { ThemeProviderSimple } from './contexts/ThemeContextSimple'
import { PortalToaster } from './components/PortalToast'
import { registerServiceWorker, setupNetworkListeners } from './lib/serviceWorker'
import { showToast } from './utils/portalToast'
import { AIProvider } from './contexts/AIContext'
import AIAssistant from './components/ai/AIAssistant'
import AIFloatingButton from './components/ai/AIFloatingButton'
import { autoRecoverAuth } from './lib/auth-recovery'
import { useAuthPersistence } from './hooks/useAuthPersistence'
import AuthStatusIndicator from './components/auth/AuthStatusIndicator'
import { handleSubdomainRedirect } from './utils/subdomainRedirect'
import { isDemoAccount } from './lib/demo'
import { CustomerThemeWrapper } from './components/customer/CustomerThemeWrapper'
import CookieConsent from './components/CookieConsent'

import LazyLoader, { 
  PageSkeleton, 
  CalendarSkeleton, 
  DashboardSkeleton,
  ListSkeleton 
} from './components/ui/LazyLoader'

// Lazy load pages for code splitting
const Login = React.lazy(() => import('./pages/Login'))
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'))
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'))
const SetPasswordWithToken = React.lazy(() => import('./pages/SetPasswordWithToken'))
const SetupAccount = React.lazy(() => import('./pages/SetupAccount'))
const Dashboard = React.lazy(() => import('./pages/Dashboard'))
const Demo = React.lazy(() => import('./pages/Demo'))
const DemoClient = React.lazy(() => import('./pages/DemoClient'))
const Landing = React.lazy(() => import('./pages/Landing'))
const DynamicLanding = React.lazy(() => import('./components/DynamicLanding'))
const Pricing = React.lazy(() => import('./pages/Pricing'))
const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation'))
const AISetupWizard = React.lazy(() => import('./components/setup/AISetupWizard'))
const Welcome = React.lazy(() => import('./pages/Welcome'))
const AIDataImport = React.lazy(() => import('./components/setup/AIDataImport'))
const QuickLaunch = React.lazy(() => import('./pages/QuickLaunch'))
const WebsitePreview = React.lazy(() => import('./pages/WebsitePreview'))
const Schedule = React.lazy(() => import('./pages/Schedule'))
const Members = React.lazy(() => import('./pages/Members'))
const MemberProfile = React.lazy(() => import('./pages/MemberProfile'))
const Facilities = React.lazy(() => import('./pages/FacilitiesSimple'))
const CustomerLayout = React.lazy(() => import('./components/customer/CustomerLayout'))
const CustomerLayoutMain = React.lazy(() => import('./components/customer/CustomerLayoutMain'))

// Customer Pages
const CustomerLanding = React.lazy(() => import('./pages/customer/Landing'))
const CustomerSchedule = React.lazy(() => import('./pages/customer/Schedule'))
const CustomerLogin = React.lazy(() => import('./pages/customer/Login'))
const CustomerActivateAccount = React.lazy(() => import('./pages/customer/ActivateAccount'))
const AccountRecovery = React.lazy(() => import('./pages/customer/AccountRecovery'))
const CustomerSetPassword = React.lazy(() => import('./pages/customer/SetPassword'))
const CustomerDashboard = React.lazy(() => import('./pages/customer/Dashboard'))
const CustomerBookClass = React.lazy(() => import('./pages/customer/BookClass'))
const CustomerInvoices = React.lazy(() => import('./pages/customer/Invoices'))
const InvoicePaymentSuccess = React.lazy(() => import('./pages/customer/InvoicePaymentSuccess'))
const CustomerRegister = React.lazy(() => import('./pages/customer/Register'))
const CustomerVerifyEmail = React.lazy(() => import('./pages/customer/VerifyEmail'))
const CustomerPackageStore = React.lazy(() => import('./pages/customer/PackageStore'))
const CustomerPurchaseSuccess = React.lazy(() => import('./pages/customer/PurchaseSuccess'))
const CustomerProfile = React.lazy(() => import('./pages/customer/Profile'))
const CustomerBookingHistory = React.lazy(() => import('./pages/customer/BookingHistory'))
const CustomerMemberships = React.lazy(() => import('./pages/customer/Memberships'))
// PublicMemberships only used on org marketing sites (subdomain), not admin app
const CustomerStore = React.lazy(() => import('./pages/customer/Store'))
const CustomerOrders = React.lazy(() => import('./pages/customer/Orders'))
const ForgotPassword = React.lazy(() => import('./pages/customer/ForgotPassword'))
const CustomerProductDetail = React.lazy(() => import('./pages/customer/ProductDetail'))
const CustomerCart = React.lazy(() => import('./pages/customer/Cart'))
const CustomerCheckout = React.lazy(() => import('./pages/customer/Checkout'))
const CustomerOrderSuccess = React.lazy(() => import('./pages/customer/OrderSuccess'))
const CustomerAllProducts = React.lazy(() => import('./pages/customer/AllProducts'))
const CustomerCategoryProducts = React.lazy(() => import('./pages/customer/CategoryProducts'))
const CustomerSearchResults = React.lazy(() => import('./pages/customer/SearchResults'))
const CustomerOrderConfirmation = React.lazy(() => import('./pages/customer/OrderConfirmation'))
const CustomerWaitlists = React.lazy(() => import('./pages/customer/Waitlists'))
const CustomerWaivers = React.lazy(() => import('./pages/customer/Waivers'))
const CustomerDocuments = React.lazy(() => import('./pages/customer/Documents'))
const CustomerFamily = React.lazy(() => import('./pages/customer/Family'))
const CustomerFamilyInviteAccept = React.lazy(() => import('./pages/customer/FamilyInviteAccept'))
const CustomerMembership = React.lazy(() => import('./pages/customer/Membership'))
const CustomerSettings = React.lazy(() => import('./pages/customer/Settings'))
const CustomerUpcomingSessions = React.lazy(() => import('./pages/customer/UpcomingSessions'))
const CustomerWaitlistResponse = React.lazy(() => import('./pages/customer/camps/WaitlistResponse'))
const ClassWaitlistResponse = React.lazy(() => import('./pages/customer/ClassWaitlistResponse'))
const Billing = React.lazy(() => import('./pages/Billing'))
const Reports = React.lazy(() => import('./pages/Reports'))
const Settings = React.lazy(() => import('./pages/Settings'))
const PaymentDemo = React.lazy(() => import('./pages/PaymentDemo'))
const VideoCallTest = React.lazy(() => import('./pages/VideoCallTest'))
const LiveTelestrationTest = React.lazy(() => import('./pages/LiveTelestrationTest'))
const RemoteSessions = React.lazy(() => import('./pages/RemoteSessions'))
const RemoteSessionRoom = React.lazy(() => import('./pages/RemoteSessionRoom'))
const Invoices = React.lazy(() => import('./pages/Invoices'))
const InvoiceCreate = React.lazy(() => import('./pages/InvoiceCreate'))
const InvoiceDetail = React.lazy(() => import('./pages/InvoiceDetail'))
const Payments = React.lazy(() => import('./pages/Payments'))
const RecordPayment = React.lazy(() => import('./pages/RecordPayment'))
const PaymentHistory = React.lazy(() => import('./pages/PaymentHistory'))
const CommunicationHistory = React.lazy(() => import('./pages/CommunicationHistory'))
const SendCommunication = React.lazy(() => import('./pages/SendCommunication'))
// Debug pages removed for production - see git history if needed
const BookingTypesManagement = React.lazy(() => import('./pages/BookingTypesManagement'))
const CalendarSettings = React.lazy(() => import('./pages/CalendarSettings'))
const Templates = React.lazy(() => import('./pages/Templates'))
const SubscriptionDashboard = React.lazy(() => import('./pages/SubscriptionDashboard'))
const Venues = React.lazy(() => import('./pages/Venues'))
const TeamManagement = React.lazy(() => import('./pages/TeamManagement'))
const TeamDashboard = React.lazy(() => import('./pages/teams/TeamDashboard'))
const TeamDetail = React.lazy(() => import('./pages/teams/TeamDetail'))
const TeamRoster = React.lazy(() => import('./pages/teams/TeamRoster'))
const TeamPracticePlans = React.lazy(() => import('./pages/teams/TeamPracticePlans'))
const TeamMessages = React.lazy(() => import('./pages/teams/TeamMessages'))
const TeamPerformance = React.lazy(() => import('./pages/teams/TeamPerformance'))

// Assistant pages
const AssistantSetup = React.lazy(() => import('./pages/assistant/AssistantSetup'))
const CallHistory = React.lazy(() => import('./pages/assistant/CallHistory'))
const ActionLog = React.lazy(() => import('./pages/assistant/ActionLog'))
const SMSInbox = React.lazy(() => import('./pages/assistant/SMSInbox'))

// Platform routes for super admins
const PlatformOrganizations = React.lazy(() => import('./pages/platform/Organizations'))
const PlatformOrganizationUsers = React.lazy(() => import('./pages/platform/OrganizationUsers'))
const SessionPackages = React.lazy(() => import('./pages/SessionPackages'))
const SalesDashboard = React.lazy(() => import('./pages/SalesDashboard'))
const Waivers = React.lazy(() => import('./pages/WaiversNew'))
const Contracts = React.lazy(() => import('./pages/Contracts'))
const Classes = React.lazy(() => import('./pages/Classes'))
const ClassesAndCamps = React.lazy(() => import('./pages/ClassesAndCamps'))
const CampManagement = React.lazy(() => import('./pages/camps/CampManagement'))
const Rentals = React.lazy(() => import('./pages/Rentals'))
const CampAnalytics = React.lazy(() => import('./pages/CampAnalytics'))
const SessionMedia = React.lazy(() => import('./pages/SessionMedia'))
const TelestrationStudio = React.lazy(() => import('./pages/TelestrationStudio'))
const CoachStudio = React.lazy(() => import('./pages/CoachStudio'))
const CoachLibrary = React.lazy(() => import('./pages/CoachLibrary'))
const CustomerTrainingLibrary = React.lazy(() => import('./pages/customer/TrainingLibrary'))
const CustomerSessionMediaDetail = React.lazy(() => import('./pages/customer/SessionMediaDetail'))
const CampRegistrationPage = React.lazy(() => import('./pages/CampRegistrationPage'))
const CustomerCamps = React.lazy(() => import('./pages/customer/Camps'))
const CustomerCampEnroll = React.lazy(() => import('./pages/customer/CampEnroll'))
const CustomerCampEnrollmentDetail = React.lazy(() => import('./pages/customer/CampEnrollmentDetail'))
const CustomerCampEnrollmentSuccess = React.lazy(() => import('./pages/customer/CampEnrollmentSuccess'))
const CustomerBookRemoteSession = React.lazy(() => import('./pages/customer/BookRemoteSession'))
const Marketing = React.lazy(() => import('./pages/Marketing'))
const CampaignBuilder = React.lazy(() => import('./pages/marketing/CampaignBuilder'))
const CampaignDetail = React.lazy(() => import('./pages/marketing/CampaignDetail'))
const Analytics = React.lazy(() => import('./pages/marketing/Analytics'))
const AttendanceReports = React.lazy(() => import('./pages/AttendanceReports'))
const InstructorPortal = React.lazy(() => import('./pages/InstructorPortal'))
const Memberships = React.lazy(() => import('./pages/Memberships'))
const LocalImportOnly = React.lazy(() => import('./components/setup/LocalImportOnly'))

// Consolidated Pages
const Finance = React.lazy(() => import('./pages/Finance'))
const Products = React.lazy(() => import('./pages/Products'))
const AnalyticsHub = React.lazy(() => import('./pages/Analytics'))

const Programs = React.lazy(() => import('./pages/Programs'))
const ScheduleConsolidated = React.lazy(() => import('./pages/ScheduleConsolidated'))
const MembersConsolidated = React.lazy(() => import('./pages/MembersConsolidated'))
const MarketingHub = React.lazy(() => import('./pages/MarketingHub'))
const MediaHub = React.lazy(() => import('./pages/MediaHub'))
const Appearance = React.lazy(() => import('./pages/Appearance'))
const PackageCatalog = React.lazy(() => import('./pages/PackageCatalog'))
const PackageManagement = React.lazy(() => import('./pages/PackageManagement'))
const MemberWaivers = React.lazy(() => import('./pages/MemberWaivers'))
const MarketingSiteSettings = React.lazy(() => import('./pages/settings/MarketingSiteSettings'))

// Client Management wrappers
const ClientsWrapper = React.lazy(() => import('./pages/ClientsWrapper'))
const ClientDetailWrapper = React.lazy(() => import('./pages/ClientDetailWrapper'))
const ClientFormWrapper = React.lazy(() => import('./pages/ClientFormWrapper'))

// Super Admin pages
const PlatformDashboard = React.lazy(() => import('./pages/admin/PlatformDashboard'))
const Organizations = React.lazy(() => import('./pages/admin/Organizations'))
const OrganizationDetail = React.lazy(() => import('./pages/admin/OrganizationDetail'))
const OrganizationCreate = React.lazy(() => import('./pages/admin/OrganizationCreate'))

// Payment pages
const ClientPaymentPage = React.lazy(() => import('./pages/payment/ClientPaymentPage'))
const PaymentSuccessPage = React.lazy(() => import('./pages/payment/PaymentSuccessPage'))
const PaymentFailurePage = React.lazy(() => import('./pages/payment/PaymentFailurePage'))

// Waiver pages
const WaiverSigningPage = React.lazy(() => import('./pages/waiver/WaiverSigningPageSimple'))
const WaiverSignedSuccess = React.lazy(() => import('./pages/waiver/WaiverSignedSuccess'))

// Account activation
const ActivateAccount = React.lazy(() => import('./pages/ActivateAccount'))

// Unsubscribe
const Unsubscribe = React.lazy(() => import('./pages/Unsubscribe'))

// OAuth callbacks
const GmailCallback = React.lazy(() => import('./pages/oauth/GmailCallback'))

// Legal and SEO pages
const NotFound = React.lazy(() => import('./pages/NotFound'))
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'))

// Shared media (public)
const SharedMedia = React.lazy(() => import('./pages/SharedMedia'))

// Contract pages
const ContractSigningPage = React.lazy(() => import('./pages/contract/ContractSigningPage'))
const ContractSignedSuccess = React.lazy(() => import('./pages/contract/ContractSignedSuccess'))

// Family Member Portal
const FamilyMemberPortal = React.lazy(() => import('./pages/FamilyMemberPortal'))

// Setup Wizard
import { SetupWizard, SetupWizardProvider } from './components/setup-wizard'
import { useSessionHealth } from './hooks/useSessionHealth'

function InvoicePayRedirect() {
  const { id } = useParams()
  return <Navigate to={`/pay/${id}`} replace />
}

function AppContent() {
  const { loading, user, profile, isMember, isStaff, isAdmin, organizationRole } = useAuth()
  const { trackPageView, trackAuth } = useMonitoring()
  
  // Monitor session health
  useSessionHealth()
  
  // Ensure auth persistence across navigation
  useAuthPersistence()
  
  // Initialize service worker on app start
  useEffect(() => {
    // Handle subdomain redirects
    handleSubdomainRedirect()

    // Check and recover auth state before anything else
    autoRecoverAuth().then(recovered => {
      if (!recovered) {
        // Register service worker
        registerServiceWorker()

        // Setup network listeners
        const cleanup = setupNetworkListeners(
          () => showToast.success('Connection restored'),
          () => showToast.warning('You are offline. Some features may be limited.')
        )

        return cleanup
      }
    })
  }, [])
  
  // Update user context in monitoring
  useEffect(() => {
    if (user && profile) {
      setUserContext({
        id: user.id,
        email: user.email,
        role: organizationRole || undefined,
        memberNumber: profile.member_number
      })
    } else {
      clearUserContext()
    }
  }, [user, profile, organizationRole])

  // Track authentication state changes
  useEffect(() => {
    if (user) {
      trackAuth('login', true)
    }
  }, [user, trackAuth])

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-primary text-text-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading AI Facility Manager...</p>
        </div>
      </div>
    )
  }

  return (
    <Router>
      <PortalToaster />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <Suspense fallback={<PageSkeleton />}>
            <DynamicLanding />
          </Suspense>
        } />
        
        <Route path="/demo" element={
          <Suspense fallback={<LazyLoader />}>
            <Demo />
          </Suspense>
        } />

        <Route path="/demo/client" element={
          <Suspense fallback={<LazyLoader />}>
            <DemoClient />
          </Suspense>
        } />

        <Route path="/login" element={
          <PortalErrorBoundary level="page">
            <Suspense fallback={<LazyLoader />}>
              <Login />
            </Suspense>
          </PortalErrorBoundary>
        } />

        <Route path="/signup" element={<Navigate to="/login?signup=true" replace />} />

        {/* Public Website Preview - org's marketing site */}
        <Route path="/preview-site" element={
          <Suspense fallback={<PageSkeleton />}>
            <WebsitePreview />
          </Suspense>
        } />
        <Route path="/preview-site/:subdomain" element={
          <Suspense fallback={<PageSkeleton />}>
            <WebsitePreview />
          </Suspense>
        } />

        <Route path="/reset-password" element={
          <Suspense fallback={<LazyLoader />}>
            <ResetPassword />
          </Suspense>
        } />

        <Route path="/verify-email" element={
          <Suspense fallback={<LazyLoader />}>
            <VerifyEmail />
          </Suspense>
        } />
        
        <Route path="/set-password" element={
          <Suspense fallback={<LazyLoader />}>
            <SetPasswordWithToken />
          </Suspense>
        } />
        
        <Route path="/setup-account" element={
          <Suspense fallback={<LazyLoader />}>
            <SetupAccount />
          </Suspense>
        } />
        
        <Route path="/pricing" element={
          <Suspense fallback={<PageSkeleton />}>
            <Pricing />
          </Suspense>
        } />

        {/* Legal Pages */}
        <Route path="/privacy" element={
          <Suspense fallback={<PageSkeleton />}>
            <PrivacyPolicy />
          </Suspense>
        } />
        <Route path="/terms" element={
          <Suspense fallback={<PageSkeleton />}>
            <TermsOfService />
          </Suspense>
        } />
        
        <Route path="/accept-invitation" element={
          <Suspense fallback={<LazyLoader />}>
            <AcceptInvitation />
          </Suspense>
        } />
        
        {/* Payment Routes - Public (accessible via payment link) */}
        <Route path="/pay/:invoiceId" element={
          <PortalErrorBoundary level="page">
            <Suspense fallback={<PageSkeleton />}>
              <ClientPaymentPage />
            </Suspense>
          </PortalErrorBoundary>
        } />
        <Route path="/payment/success" element={
          <Suspense fallback={<PageSkeleton />}>
            <PaymentSuccessPage />
          </Suspense>
        } />
        <Route path="/payment/failure" element={
          <Suspense fallback={<PageSkeleton />}>
            <PaymentFailurePage />
          </Suspense>
        } />
        
        {/* Account Activation Route - Public */}
        <Route path="/activate/:token" element={
          <PortalErrorBoundary level="page">
            <Suspense fallback={<PageSkeleton />}>
              <ActivateAccount />
            </Suspense>
          </PortalErrorBoundary>
        } />
        
        {/* Unsubscribe Route - Public */}
        <Route path="/unsubscribe/:token" element={
          <Suspense fallback={<PageSkeleton />}>
            <Unsubscribe />
          </Suspense>
        } />

        {/* OAuth Callbacks */}
        <Route path="/oauth/gmail/callback" element={
          <Suspense fallback={<PageSkeleton />}>
            <GmailCallback />
          </Suspense>
        } />

        {/* Waiver Routes - Public (accessible via signing link) - With theme */}
        <Route path="/waivers/sign/:token" element={
          <PortalErrorBoundary level="page">
            <CustomerThemeWrapper>
              <Suspense fallback={<PageSkeleton />}>
                <WaiverSigningPage />
              </Suspense>
            </CustomerThemeWrapper>
          </PortalErrorBoundary>
        } />
        <Route path="/waiver-signed-success" element={
          <CustomerThemeWrapper>
            <Suspense fallback={<PageSkeleton />}>
              <WaiverSignedSuccess />
            </Suspense>
          </CustomerThemeWrapper>
        } />
        
        {/* Contract Routes - Public (accessible via signing link) - With theme */}
        <Route path="/contracts/sign/:token" element={
          <PortalErrorBoundary level="page">
            <CustomerThemeWrapper>
              <Suspense fallback={<PageSkeleton />}>
                <ContractSigningPage />
              </Suspense>
            </CustomerThemeWrapper>
          </PortalErrorBoundary>
        } />
        <Route path="/contract-signed-success" element={
          <CustomerThemeWrapper>
            <Suspense fallback={<PageSkeleton />}>
              <ContractSignedSuccess />
            </Suspense>
          </CustomerThemeWrapper>
        } />
        
        {/* Package Catalog - Public route for browsing and purchasing packages */}
        <Route path="/packages" element={
          <Suspense fallback={<PageSkeleton />}>
            <PackageCatalog />
          </Suspense>
        } />

        {/* Shared Media - Public route (no auth required) */}
        <Route path="/shared/:token" element={
          <Suspense fallback={<PageSkeleton />}>
            <SharedMedia />
          </Suspense>
        } />

        {/* Customer Routes - All wrapped with CustomerLayout for theme */}
        <Route path="/customer" element={<CustomerLayout />}>
          <Route index element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerLanding />
            </Suspense>
          } />
          <Route path="schedule" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerSchedule />
            </Suspense>
          } />
          <Route path="login" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerLogin />
            </Suspense>
          } />
          <Route path="activate/:token" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerActivateAccount />
            </Suspense>
          } />
          <Route path="account-recovery" element={
            <Suspense fallback={<PageSkeleton />}>
              <AccountRecovery />
            </Suspense>
          } />
          <Route path="set-password" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerSetPassword />
            </Suspense>
          } />
          <Route path="family-invite-accept" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerFamilyInviteAccept />
            </Suspense>
          } />
          <Route path="register" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerRegister />
            </Suspense>
          } />
          <Route path="verify-email" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerVerifyEmail />
            </Suspense>
          } />
          <Route path="forgot-password" element={
            <Suspense fallback={<PageSkeleton />}>
              <ForgotPassword />
            </Suspense>
          } />
          <Route path="camps/waitlist-response" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerWaitlistResponse />
            </Suspense>
          } />
          <Route path="waitlist/respond/:token" element={
            <Suspense fallback={<PageSkeleton />}>
              <ClassWaitlistResponse />
            </Suspense>
          } />
          <Route path="packages/purchase" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerPackageStore />
            </Suspense>
          } />
          <Route path="purchase-success" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerPurchaseSuccess />
            </Suspense>
          } />
          <Route path="history" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerBookingHistory />
            </Suspense>
          } />
          <Route path="memberships" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerMemberships />
            </Suspense>
          } />
          
          {/* Customer Store Routes */}
          <Route path="store" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerStore />
            </Suspense>
          } />
          <Route path="store/orders" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerOrders />
            </Suspense>
          } />
          <Route path="store/products/:slug" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerProductDetail />
            </Suspense>
          } />
          <Route path="store/cart" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCart />
            </Suspense>
          } />
          <Route path="store/checkout" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCheckout />
            </Suspense>
          } />
          <Route path="store/products" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerAllProducts />
            </Suspense>
          } />
          <Route path="store/category/:slug" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCategoryProducts />
            </Suspense>
          } />
          <Route path="store/search" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerSearchResults />
            </Suspense>
          } />
          <Route path="store/order-confirmation" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerOrderConfirmation />
            </Suspense>
          } />
          <Route path="store/order/:orderId" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerOrderSuccess />
            </Suspense>
          } />
        </Route>

        {/* Welcome Page - setup choice */}
        <Route path="/welcome" element={
          <AuthGuard requiredRoles={['admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <Welcome />
            </Suspense>
          </AuthGuard>
        } />

        {/* Quick Launch Wizard - unified onboarding */}
        <Route path="/quick-launch" element={
          <AuthGuard requiredRoles={['admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <QuickLaunch />
            </Suspense>
          </AuthGuard>
        } />

        {/* AI Setup route alias */}
        <Route path="/setup/ai" element={<Navigate to="/ai-setup" replace />} />

        {/* Setup Wizard - outside of Layout */}
        <Route path="/setup" element={
          <AuthGuard requiredRoles={['admin']}>
            <SetupWizardProvider>
              <SetupWizard />
            </SetupWizardProvider>
          </AuthGuard>
        } />
        
        {/* Customer Routes - Main App */}
        <Route path="/customer" element={
          <AuthGuard requiredRoles={['member']}>
            <Suspense fallback={<PageSkeleton />}>
              <CustomerLayoutMain />
            </Suspense>
          </AuthGuard>
        }>
          <Route path="dashboard" element={
            <Suspense fallback={<DashboardSkeleton />}>
              <CustomerDashboard />
            </Suspense>
          } />
          <Route path="book" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerBookClass />
            </Suspense>
          } />
          <Route path="upcoming" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerUpcomingSessions />
            </Suspense>
          } />
          {/* Redirect old routes to My Schedule page */}
          <Route path="training" element={<Navigate to="/customer/upcoming" replace />} />
          <Route path="bookings" element={<Navigate to="/customer/upcoming" replace />} />
          <Route path="packages" element={<Navigate to="/customer/upcoming" replace />} />
          <Route path="profile" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerProfile />
            </Suspense>
          } />
          <Route path="store" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerStore />
            </Suspense>
          } />
          <Route path="store/product/:id" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerProductDetail />
            </Suspense>
          } />
          <Route path="store/cart" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCart />
            </Suspense>
          } />
          <Route path="store/checkout" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCheckout />
            </Suspense>
          } />
          <Route path="waitlists" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerWaitlists />
            </Suspense>
          } />
          <Route path="documents" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerDocuments />
            </Suspense>
          } />
          <Route path="waivers" element={<Navigate to="/customer/documents" replace />} />
          <Route path="family" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerFamily />
            </Suspense>
          } />
          <Route path="membership" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerMembership />
            </Suspense>
          } />
          <Route path="settings" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerSettings />
            </Suspense>
          } />
          <Route path="book-class" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerBookClass />
            </Suspense>
          } />
          <Route path="invoices" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerInvoices />
            </Suspense>
          } />
          <Route path="invoices/payment-success" element={
            <Suspense fallback={<PageSkeleton />}>
              <InvoicePaymentSuccess />
            </Suspense>
          } />
          <Route path="camps" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCamps />
            </Suspense>
          } />
          <Route path="camps/:campId/enroll" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCampEnroll />
            </Suspense>
          } />
          <Route path="camps/:enrollmentId/details" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCampEnrollmentDetail />
            </Suspense>
          } />
          <Route path="camps/enrollment-success" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerCampEnrollmentSuccess />
            </Suspense>
          } />
          <Route path="book-session/:bookingId" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerBookRemoteSession />
            </Suspense>
          } />
          <Route path="training-library" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerTrainingLibrary />
            </Suspense>
          } />
          <Route path="training/:mediaId" element={
            <Suspense fallback={<PageSkeleton />}>
              <CustomerSessionMediaDetail />
            </Suspense>
          } />
        </Route>
        
        {/* Protected Routes with Layout */}
        <Route element={
          <AuthGuard>
            <Layout />
          </AuthGuard>
        }>
          {/* Dashboard - accessible to all authenticated users */}
          <Route path="/dashboard" element={
            <Suspense fallback={<DashboardSkeleton />}>
              <Dashboard />
            </Suspense>
          } />
          
          {/* Consolidated Routes */}
          <Route path="operations" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <ScheduleConsolidated />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="programs" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Programs />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="members" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <MembersConsolidated />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="products" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Products />
              </Suspense>
            </AuthGuard>
          } />

          <Route path="finance" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Finance />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="analytics" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <AnalyticsHub />
              </Suspense>
            </AuthGuard>
          } />

          <Route path="marketing-hub" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <MarketingHub />
              </Suspense>
            </AuthGuard>
          } />

          <Route path="media-hub" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <MediaHub />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="settings/marketing-site" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <MarketingSiteSettings />
              </Suspense>
            </AuthGuard>
          } />

          <Route path="appearance" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <Appearance />
              </Suspense>
            </AuthGuard>
          } />

          {/* Super Admin Routes */}
          <Route path="admin" element={
            <AuthGuard requiredRoles={['admin']} requireSuperAdmin>
              <Suspense fallback={<DashboardSkeleton />}>
                <PlatformDashboard />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="admin/organizations" element={
            <AuthGuard requiredRoles={['admin']} requireSuperAdmin>
              <Suspense fallback={<ListSkeleton />}>
                <Organizations />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="admin/organizations/new" element={
            <AuthGuard requiredRoles={['admin']} requireSuperAdmin>
              <Suspense fallback={<PageSkeleton />}>
                <OrganizationCreate />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="admin/organizations/:id" element={
            <AuthGuard requiredRoles={['admin']} requireSuperAdmin>
              <Suspense fallback={<PageSkeleton />}>
                <OrganizationDetail />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Platform Routes for Super Admins */}
          <Route path="platform/organizations" element={
            <AuthGuard requiredRoles={['admin']} requireSuperAdmin>
              <Suspense fallback={<ListSkeleton />}>
                <PlatformOrganizations />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="platform/organization-users" element={
            <AuthGuard requiredRoles={['admin']} requireSuperAdmin>
              <Suspense fallback={<ListSkeleton />}>
                <PlatformOrganizationUsers />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Schedule - accessible to all authenticated users */}
          <Route path="/schedule" element={
            <Suspense fallback={<CalendarSkeleton />}>
              <Schedule />
            </Suspense>
          } />
          
          {/* Facilities Management - admin only */}
          <Route path="facilities" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <Facilities />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Local Import */}
          <Route path="local-import" element={
            <Suspense fallback={<PageSkeleton />}>
              <LocalImportOnly />
            </Suspense>
          } />
          
          {/* Classes and Camps - accessible to admin and staff only */}
          <Route path="/classes-camps" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<ListSkeleton />}>
                <ClassesAndCamps />
              </Suspense>
            </AuthGuard>
          } />

          {/* Classes - accessible to admin and staff only */}
          <Route path="/classes" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<ListSkeleton />}>
                <Classes />
              </Suspense>
            </AuthGuard>
          } />

          {/* Practice Plans & Drill Library - org level, no team required */}
          <Route path="/practice-plans" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <TeamPracticePlans />
              </Suspense>
            </AuthGuard>
          } />

          {/* Teams - accessible to admin and staff only */}
          <Route path="/teams" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<ListSkeleton />}>
                <TeamDashboard />
              </Suspense>
            </AuthGuard>
          } />

          {/* Team Detail with nested routes */}
          <Route path="/teams/:id/*" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <TeamDetail />
              </Suspense>
            </AuthGuard>
          }>
            <Route index element={<Navigate to="roster" replace />} />
            <Route path="roster" element={
              <Suspense fallback={<ListSkeleton />}>
                <TeamRoster />
              </Suspense>
            } />
            <Route path="practice-plans" element={
              <Suspense fallback={<PageSkeleton />}>
                <TeamPracticePlans />
              </Suspense>
            } />
            <Route path="messages" element={
              <Suspense fallback={<PageSkeleton />}>
                <TeamMessages />
              </Suspense>
            } />
            <Route path="performance" element={
              <Suspense fallback={<PageSkeleton />}>
                <TeamPerformance />
              </Suspense>
            } />
          </Route>

          {/* Session Media - accessible to admin and staff only */}
          <Route path="session-media" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <SessionMedia />
              </Suspense>
            </AuthGuard>
          } />

          {/* Telestration Studio - accessible to admin and staff only */}
          <Route path="session-media/:mediaId/annotate" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <TelestrationStudio />
              </Suspense>
            </AuthGuard>
          } />

          {/* Coach Studio - Still accessible for recording (hidden from sidebar nav) */}
          <Route path="coach-studio" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <CoachStudio />
              </Suspense>
            </AuthGuard>
          } />

          {/* Coach Library - accessible to admin and staff only */}
          <Route path="coach-library" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <CoachLibrary />
              </Suspense>
            </AuthGuard>
          } />

          {/* Rentals - accessible to admin and staff only */}
          <Route path="rentals" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<ListSkeleton />}>
                <Rentals />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Camp Management - accessible to admin and staff only */}
          <Route path="camps/:campId" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <CampManagement />
              </Suspense>
            </AuthGuard>
          } />
          
          
          {/* Camp Analytics - accessible to admin only */}
          <Route path="camp-analytics" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<DashboardSkeleton />}>
                <CampAnalytics />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Camp Registration - accessible to all authenticated users */}
          <Route path="camps/:campId/register" element={
            <AuthGuard>
              <Suspense fallback={<PageSkeleton />}>
                <CampRegistrationPage />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Coach Studio (formerly Session Media Hub) - accessible to admin and staff only */}
          <Route path="session-media" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <SessionMedia />
              </Suspense>
            </AuthGuard>
          } />

          {/* Attendance Reports - accessible to admin and staff only */}
          <Route path="attendance" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <AttendanceReports />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Instructor Portal - accessible to staff only */}
          <Route path="instructor" element={
            <AuthGuard requiredRoles={['staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <InstructorPortal />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Clients - accessible to admin and staff only */}
          <Route path="clients" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<ListSkeleton />}>
                <ClientsWrapper />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* New Client */}
          <Route path="clients/new" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <ClientFormWrapper />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Edit Client */}
          <Route path="clients/:id/edit" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <ClientFormWrapper />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Client Profile - accessible to admin and staff only */}
          <Route path="clients/:id" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <ClientDetailWrapper />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Redirect old members route to clients */}
          <Route path="members" element={<Navigate to="/clients" replace />} />
          <Route path="members/:id" element={<Navigate to="/clients/:id" replace />} />
          
          {/* Billing - accessible to admin and staff only, but not trainers */}
          <Route path="billing" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <FacilityOnlyRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <Billing />
                </Suspense>
              </FacilityOnlyRoute>
            </AuthGuard>
          } />
          
          {/* Subscription - accessible to admin only */}
          <Route path="subscription" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <SubscriptionDashboard />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Team Management - accessible to admin only, facilities only */}
          <Route path="team" element={
            <AuthGuard requiredRoles={['admin']}>
              <FacilityOnlyRoute>
                <Suspense fallback={<ListSkeleton />}>
                  <TeamManagement />
                </Suspense>
              </FacilityOnlyRoute>
            </AuthGuard>
          } />
          
          {/* Invoices - redirect to Billing page */}
          <Route path="invoices" element={<Navigate to="/billing?tab=invoices" replace />} />
          <Route path="invoices/new" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <InvoiceCreate />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="invoices/:id" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <InvoiceDetail />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Payments - redirect to Billing page */}
          <Route path="payments" element={<Navigate to="/billing?tab=payments" replace />} />
          <Route path="payments/record" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <RecordPayment />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="payments/history" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<ListSkeleton />}>
                <PaymentHistory />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Communications - accessible to admin and staff only */}
          <Route path="communications/history" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<ListSkeleton />}>
                <CommunicationHistory />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="communications/send" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <SendCommunication />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Reports - accessible to admin and staff only */}
          <Route path="reports" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Reports />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="reports/financial" element={<Navigate to="/reports" replace />} />
          
          {/* Facilities - accessible to all authenticated users */}
          <Route path="facilities" element={
            <FacilityOnlyRoute>
              <Suspense fallback={<PageSkeleton />}>
                <Facilities />
              </Suspense>
            </FacilityOnlyRoute>
          } />
          
          {/* Venues - for trainer mode only */}
          <Route path="venues" element={
            <TrainerOnlyRoute>
              <Suspense fallback={<PageSkeleton />}>
                <Venues />
              </Suspense>
            </TrainerOnlyRoute>
          } />
          
          {/* Session Packages - for trainer mode only */}
          <Route path="session-packages" element={
            <TrainerOnlyRoute>
              <Suspense fallback={<PageSkeleton />}>
                <SessionPackages />
              </Suspense>
            </TrainerOnlyRoute>
          } />
          
          {/* Sales Dashboard - accessible to admin and staff */}
          <Route path="sales" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <SalesDashboard />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Package Management - accessible to admin and staff */}
          <Route path="package-management" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <PackageManagement />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Waivers - accessible to admin and staff */}
          <Route path="waivers" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Waivers />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Contracts - accessible to admin and staff */}
          <Route path="contracts" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Contracts />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Marketing - accessible to admin and staff */}
          <Route path="marketing" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Marketing />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Campaign Builder */}
          <Route path="marketing/campaign/new" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <CampaignBuilder />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="marketing/campaign/:id" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <CampaignDetail />
              </Suspense>
            </AuthGuard>
          } />
          
          <Route path="marketing/campaign/:id/edit" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <CampaignBuilder />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Analytics */}
          <Route path="marketing/analytics" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Analytics />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Virtual Assistant Routes */}
          <Route path="assistant" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <AssistantSetup />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="assistant/calls" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <CallHistory />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="assistant/actions" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <ActionLog />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="assistant/sms" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <SMSInbox />
              </Suspense>
            </AuthGuard>
          } />

          {/* Booking Types route removed - now handled by generic settings/:section route */}
          
          {/* Calendar Settings - accessible to admin only */}
          <Route path="settings/calendar" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <CalendarSettings />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Templates - accessible to admin only */}
          <Route path="settings/templates" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <Templates />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Staff - redirect to team management */}
          <Route path="staff" element={<Navigate to="/team" replace />} />
          
          {/* Settings - accessible to admin only */}
          <Route path="settings" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <Settings />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="settings/:section" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <Settings />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Redirects for consolidated Appearance page */}
          <Route path="settings/branding" element={<Navigate to="/appearance" replace />} />
          <Route path="settings/business" element={<Navigate to="/appearance" replace />} />
          <Route path="settings/theme" element={<Navigate to="/appearance" replace />} />
          
          {/* Memberships - accessible to admin and staff only */}
          <Route path="memberships" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <Memberships />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* Payment Demo - accessible to admin and staff only */}
          <Route path="payment-demo" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <PaymentDemo />
              </Suspense>
            </AuthGuard>
          } />

          {/* Video Call Test - accessible to admin and staff only */}
          <Route path="video-call-test" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <VideoCallTest />
              </Suspense>
            </AuthGuard>
          } />

          {/* Live Telestration Test - accessible to admin and staff only */}
          <Route path="live-telestration-test" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <LiveTelestrationTest />
              </Suspense>
            </AuthGuard>
          } />

          {/* Remote Sessions - accessible to admin and staff */}
          <Route path="remote-sessions" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <RemoteSessions />
              </Suspense>
            </AuthGuard>
          } />

          {/* Remote Session Room - accessible to admin and staff */}
          <Route path="remote-session/:sessionId" element={
            <AuthGuard requiredRoles={['admin', 'staff']}>
              <Suspense fallback={<PageSkeleton />}>
                <RemoteSessionRoom />
              </Suspense>
            </AuthGuard>
          } />

          {/* AI Setup Wizard - for quick setup */}
          <Route path="ai-setup" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <AISetupWizard />
              </Suspense>
            </AuthGuard>
          } />
          
          {/* AI Data Import - for importing existing data */}
          <Route path="ai-import" element={
            <AuthGuard requiredRoles={['admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <AIDataImport onComplete={() => window.location.href = '/dashboard'} />
              </Suspense>
            </AuthGuard>
          } />

        </Route>

        {/* Family Member Portal - Restricted Access */}
        <Route path="/family-member-portal" element={
          <Suspense fallback={<PageSkeleton />}>
            <FamilyMemberPortal />
          </Suspense>
        } />


        {/* Redirect old invoice payment URL pattern to new /pay/:id */}
        <Route path="/invoices/:id/pay" element={<InvoicePayRedirect />} />

        {/* Member Routes - Redirect to Customer Portal */}
        <Route path="/member" element={<Navigate to="/customer/dashboard" replace />} />
        <Route path="/member/dashboard" element={<Navigate to="/customer/dashboard" replace />} />
        <Route path="/member/book-class" element={<Navigate to="/customer/book-class" replace />} />
        <Route path="/member/invoices" element={<Navigate to="/customer/invoices" replace />} />
        <Route path="/member/packages" element={<Navigate to="/customer/packages" replace />} />
        <Route path="/member/profile" element={<Navigate to="/customer/profile" replace />} />
        <Route path="/member/waitlists" element={<Navigate to="/customer/waitlists" replace />} />
        <Route path="/member/waivers" element={<Navigate to="/customer/waivers" replace />} />
        <Route path="/member/family" element={<Navigate to="/customer/family" replace />} />
        <Route path="/member/membership" element={<Navigate to="/customer/membership" replace />} />
        <Route path="/member/settings" element={<Navigate to="/customer/settings" replace />} />
        <Route path="/member/*" element={<Navigate to="/customer/dashboard" replace />} />

        {/* 404 Not Found - catch all unmatched routes */}
        <Route path="*" element={
          <Suspense fallback={<PageSkeleton />}>
            <NotFound />
          </Suspense>
        } />
      </Routes>
    </Router>
  )
}

// Component to conditionally render AI features
function AIFeatures() {
  const { profile } = useAuth()
  // Get location from window since we're outside Router context
  const pathname = window.location.pathname

  // Don't show AI assistant on public pages
  // Note: '/' is handled separately with exact match to avoid matching all paths
  const publicPaths = [
    '/pricing',
    '/login',
    '/signup',
    '/demo',
    '/customer/login',
    '/customer/activate',
    '/customer/book-class',
    '/customer/set-password',
    '/customer/forgot-password',
    '/unsubscribe',
    '/waiver/sign',
    '/public',
    '/reset-password',
    '/set-password',
    '/shared'
  ]

  // Hide on landing page root (exact match)
  if (pathname === '/') {
    return null
  }

  // Check if current path starts with any public path prefix
  const isPublicPage = publicPaths.some(path => pathname.startsWith(path))

  if (isPublicPage) {
    return null
  }

  // Demo users see the Aria phone call, not the text chat assistant
  if (isDemoAccount(profile?.email)) {
    return null
  }

  return (
    <>
      <AIAssistant />
      <AIFloatingButton />
    </>
  )
}

// Main App component with error boundaries
function App() {
  return (
    <PortalErrorBoundary
      level="critical"
      onError={(error, errorInfo) => {
        // Additional error logging for critical app errors
        console.error('Critical App Error:', error, errorInfo)
      }}
    >
      <ThemeProviderSimple>
        <AIProvider>
          <AppContent />
          <AuthStatusIndicator />
          <AIFeatures />
          <CookieConsent />
        </AIProvider>
      </ThemeProviderSimple>
    </PortalErrorBoundary>
  )
}

export default App
